import { baseUrl } from '../api';
import { refreshAccessToken } from './tokenRefresh';
import { clearUser, clearAccessToken } from '../redux/slices/user';
import { store } from '../redux/store';

let isLoggingOut = false;

function forceLogout() {
    if (isLoggingOut) return; // prevent multiple redirects
    isLoggingOut = true;

    store.dispatch(clearUser());
    store.dispatch(clearAccessToken());

    // if (window.location.pathname !== '/login') {
    //     window.location.replace('/login');
    // }
}

async function doRequest<T>(fullUrl: string, options: RequestInit, retry: boolean): Promise<T | null> {
    const userState = store.getState().user.user;

    // 🚨 Guard: if no tokens, don’t even try request
    if (!userState?.accessToken || !userState?.refreshToken) {
        if (window.location.pathname !== '/login') {
            forceLogout();
        }
        return null;
    }

    const { accessToken, refreshToken } = userState;

    const buildHeaders = (token?: string, body?: BodyInit | null): Record<string, string> => {
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        // 🚨 only set Content-Type if body is NOT FormData
        if (!(body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }
        return headers;
    };

    let res = await fetch(fullUrl, {
        ...options,
        headers: buildHeaders(accessToken, options.body),
        credentials: 'include'
    });

    if (res.status === 401 && retry) {
        try {
            const newAccessToken = await refreshAccessToken(accessToken, refreshToken);
            if (!newAccessToken) throw new Error('refresh failed');

            res = await fetch(fullUrl, {
                ...options,
                headers: buildHeaders(newAccessToken, options.body),
                credentials: 'include'
            });
        } catch {
            forceLogout();
            return null;
        }
    }

    if (!res.ok) {
        const errorBody = await res.text();
        throw new Error(`${options.method ?? 'GET'} failed: ${res.status} ${res.statusText} - ${errorBody}`);
    }

    return res.json();
}

/**
 * Public request (no auth). Use for endpoints that must work for unauthenticated users (e.g. GetHomeAdv).
 */
async function publicRequest<T>(url: string, options: RequestInit = {}): Promise<T | null> {
    const headers: Record<string, string> = {
        ...(options.headers as Record<string, string>)
    };
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, {
        ...options,
        headers,
        credentials: 'include'
    });
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET failed: ${res.status} ${res.statusText} - ${text}`);
    }
    return res.status === 204 ? null : res.json();
}

export async function requestWithBaseUrlPublic<T = unknown>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T | null> {
    return publicRequest<T>(`${baseUrl}${endpoint}`, options);
}

export async function requestWithBaseUrl<T = unknown>(
    endpoint: string, // relative path, not full url
    options: RequestInit = {},
    retry = true
): Promise<T | null> {
    return doRequest<T>(`${baseUrl}${endpoint}`, options, retry);
}

export async function request<T = unknown>(url: string, options: RequestInit = {}, retry = true): Promise<T | null> {
    return doRequest<T>(url, options, retry);
}

export async function postWithoutToken(url: string, data = {}) {
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
    });

    const json = await response.json();

    // unwrap known shapes
    if (Array.isArray(json)) return json;
    if (json?.data && Array.isArray(json.data)) return json.data;

    console.warn('unexpected response shape', json);
    return [];
}
